<script setup lang="ts">
import sanitizeHtml from 'sanitize-html';
const { $formatDate, $axios } = useNuxtApp();

interface PostItem {
  featured_image_url?: string;
  link?: string;
  date?: string;
  id?: string | number;
  slug?: string | number;
  title?: {
    rendered?: string;
  };
  excerpt?: {
    rendered?: string;
  };
}

interface PostsData {
  list?: PostItem[];
  pagination: {
    currentPage?: number;
    total: number;
    totalPages: number;
  };
}

const currentPage = ref(1);
const perPage = 12;
const postsList = ref<HTMLElement | null>(null);

const { data: stickyPosts } = useAsyncData('stickyPosts', () =>
  $axios.get<null, PostsData>('/wp-json/wp/v2/posts', {
    params: {
      sticky: 'true',
    },
  })
);

const { data: posts, refresh } = useAsyncData('posts', () =>
  $axios.get<null, PostsData>('/wp-json/wp/v2/posts', {
    params: {
      page: currentPage.value,
      per_page: perPage,
    },
  })
);

const scrollToPostsList = () => {
  if (postsList.value) {
    postsList.value.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
};

const changePage = (page: number) => {
  currentPage.value = page;
  scrollToPostsList();
};

watch(currentPage, async () => {
  refresh();

  scrollToPostsList();
});
</script>

<template>
  <section class="relative z-10 bg-grad-cream pb-16 pt-15 md:py-14">
    <div class="container lg:py-4">
      <ul
        v-if="stickyPosts?.list?.length"
        class="relative grid grid-cols-1 gap-10 md:grid-cols-3 lg:gap-x-[155px] lg:pt-7 xl:px-18"
      >
        <li
          v-for="stickyPost in stickyPosts.list"
          :key="stickyPost.id"
          class="mb-10 md:col-span-3 md:flex md:gap-8 md:pt-1 xl:-mx-18 xl:gap-[135px]"
        >
          <NuxtLink
            v-if="stickyPost.featured_image_url"
            :to="`/blog/${stickyPost.slug}`"
            class="mb-9 block max-w-[440px] flex-shrink-0 origin-center transition-all duration-1000 hover:scale-105 md:mb-0 md:w-1/2"
          >
            <svg
              class="z-10aspect-square relative h-auto w-full"
              width="440"
              height="432"
              fill="none"
              viewBox="0 0 304 298"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <defs>
                <clipPath id="clip-path-main" key="clip-main">
                  <path
                    d="M170.791 1.01753C149 -0.477125 131.511 -1.08754 110.507 4.71758C81.1671 12.8221 52.804 23.566 31.7922 46.5112C15.4858 64.3314 4.13735 97.7533 0.970517 121.532C-3.62912 156.104 8.50904 196.214 29.1545 224.373C59.8215 266.215 108.57 285.023 158.164 295.2C185.021 300.714 211.52 298.05 236.83 287.509C264.965 275.793 275.239 257.641 284.17 229.627C293.915 199.064 302.748 165.617 303.961 133.491C305.092 103.356 281.597 70.9299 260.561 50.7861C242.822 33.7918 221.827 19.0563 199.219 9.69687C190.923 6.264 179.73 2.03767 170.791 1.02562V1.01753Z"
                  />
                </clipPath>
              </defs>
              <image
                x="0"
                y="0"
                width="440"
                height="432"
                class="h-full w-full object-cover"
                preserveAspectRatio="xMidYMid slice"
                :xlink:href="stickyPost?.featured_image_url"
                clip-path="url(#clip-path-main)"
              />
            </svg>
          </NuxtLink>
          <div class="self-center md:pb-8">
            <h3
              v-if="stickyPost.title"
              class="title mb-1 !text-current md:mb-3.5 md:text-[64px] md:leading-[1.093]"
            >
              <NuxtLink
                :to="`/blog/${stickyPost.slug}`"
                class="decoration-dotted decoration-1 underline-offset-4 hover:underline"
              >
                <span
                  v-html="sanitizeHtml(stickyPost.title.rendered || '')"
                ></span>
              </NuxtLink>
            </h3>
            <div
              v-if="stickyPost?.excerpt?.rendered"
              class="mb-5.5 md:text-xl"
              v-html="sanitizeHtml(stickyPost.excerpt.rendered)"
            />
            <ButtonLink
              :to="`/blog/${stickyPost.slug}`"
              title="Read more"
              icon="arrow-r"
            />
          </div>
        </li>
      </ul>
      <ul
        v-if="posts?.list?.length"
        ref="postsList"
        class="relative grid grid-cols-1 gap-18 overflow-hidden pt-18 md:grid-cols-3 md:gap-10 lg:gap-x-[155px] xl:px-18"
      >
        <li
          v-for="(post, postIndex) in posts.list"
          :key="post.id"
          class="md:mb-16"
        >
          <NuxtLink
            v-if="post.featured_image_url"
            :to="`/blog/${post.slug}`"
            class="group"
          >
            <svg
              class="relative z-10 mb-9 aspect-square h-auto w-full origin-center transition-all duration-1000 group-hover:scale-105"
              width="304"
              height="298"
              fill="none"
              viewBox="0 0 304 298"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <defs>
                <clipPath
                  :id="`clip-path-${postIndex}`"
                  :key="`clip-${postIndex}`"
                >
                  <path
                    v-if="postIndex % 10 === 0"
                    d="M171.353 1.01748C149.49 -0.477172 131.943 -1.08758 110.871 4.71754C81.4341 12.822 52.9777 23.566 31.8968 46.5112C15.5368 64.3313 4.15096 97.7533 0.973709 121.532C-3.64106 156.104 8.53703 196.214 29.2504 224.373C60.0183 266.215 108.927 285.023 158.685 295.2C185.63 300.714 212.216 298.05 237.609 287.509C265.837 275.793 276.145 257.641 285.105 229.627C294.882 199.064 303.744 165.617 304.961 133.491C306.096 103.356 282.524 70.9299 261.418 50.7861C243.621 33.7917 222.556 19.0563 199.874 9.69683C191.551 6.26395 180.321 2.03763 171.353 1.02558V1.01748Z"
                  />
                  <path
                    v-else-if="postIndex % 10 === 1"
                    d="M98.1012 292.053C79.3391 287.085 59.5791 280.633 43.3902 269.686C14.8389 250.363 3.96922 213.318 0.810601 180.685C-5.12728 119.283 21.7087 49.8035 76.3372 18.0078C139.724 -18.8799 221.791 3.76027 265.78 59.9376C289.045 89.6435 300.327 119.515 302.282 157.216C303.14 173.735 304.022 189.177 299.857 205.291C295.016 224.025 286.027 234.449 272.279 247.768C247.901 271.378 216.636 287.11 183.862 295.76C174.189 298.314 164.078 298.206 154.123 297.734C151.394 297.601 110.967 295.453 98.1012 292.053Z"
                  />
                  <path
                    v-else-if="postIndex % 10 === 2"
                    d="M164.171 0.693711C127.03 -2.11816 72.6477 2.2448 50.6601 39.7696C28.8994 76.9211 7.27154 117.1 0.961468 161.319C-5.13749 204.069 18.4764 229.085 50.4178 250.726C84.4312 273.768 121.205 292.489 161.716 296.545C195.847 299.971 221.877 298.578 250.894 278.256C277.229 259.809 287.73 228.571 296.604 197.807C316.536 128.762 279.035 56.4998 220.649 24.1674C208.686 17.54 200.108 9.63529 187.136 5.6207C182.601 4.21892 171.396 1.24115 164.171 0.693711Z"
                  />
                  <path
                    v-else-if="postIndex % 10 === 3"
                    d="M101.748 290.053C82.8624 285.085 64.9996 273.667 46.9996 257C19.9996 232 12.9996 217.5 4.81555 178.685C-5.97667 127.5 2.51049 51.2957 57.4996 19.5C121.305 -17.3877 206.719 2.82258 251 58.9999C274.418 88.7058 289.532 110.3 291.5 148C292.363 164.52 286.192 189.887 282 206C277.127 224.734 258.838 246.681 245 260C220.46 283.61 213.066 285.11 180.076 293.76C170.338 296.314 160.16 296.206 150.14 295.733C147.393 295.601 114.699 293.453 101.748 290.053Z"
                  />
                  <path
                    v-else-if="postIndex % 10 === 4"
                    d="M177.995 4.62075C122.602 -9.11744 62.1259 14.9753 39.9202 52.5001C17.9435 89.6516 8.37283 111.781 2.00013 156C-4.15935 198.75 12.6619 231.085 44.9202 252.726C79.2712 275.768 111.41 290.489 152.323 294.546C186.792 297.971 213.081 297.578 242.386 277.256C268.982 258.809 284.537 225.765 293.5 195C313.629 125.956 285.465 58.3324 226.5 26.0001C214.418 19.3727 196.298 9.16002 177.995 4.62075Z"
                  />
                  <path
                    v-else-if="postIndex % 10 === 5"
                    d="M166.353 1.0175C144.49 -0.477155 121.396 2.60031 95.5003 13.5C44.3545 35.028 7.006 83 2.00033 120.5C-3.40578 161 5.28695 210.841 26.0003 239C56.7682 280.842 99.3555 299.252 153.685 295.2C210 291 250.618 272.674 266.5 232.5C275 211 298.744 165.617 299.961 133.491C301.096 103.356 279.606 60.6439 258.5 40.5C240.703 23.5057 217.557 16.0563 194.875 6.69684C186.552 3.26397 175.321 2.03765 166.353 1.0256V1.0175Z"
                  />
                  <path
                    v-else-if="postIndex % 10 === 6"
                    d="M171.353 1.01748C149.49 -0.477172 131.943 -1.08758 110.871 4.71754C81.4341 12.822 52.9777 23.566 31.8968 46.5112C15.5368 64.3313 4.15096 97.7533 0.973709 121.532C-3.64106 156.104 8.53703 196.214 29.2504 224.373C60.0183 266.215 108.927 285.023 158.685 295.2C185.63 300.714 212.216 298.05 237.609 287.509C265.837 275.793 276.145 257.641 285.105 229.627C294.882 199.064 303.744 165.617 304.961 133.491C306.096 103.356 282.524 70.9299 261.418 50.7861C243.621 33.7917 222.556 19.0563 199.874 9.69683C191.551 6.26395 180.321 2.03763 171.353 1.02558V1.01748Z"
                  />
                  <path
                    v-else-if="postIndex % 10 === 7"
                    d="M164.171 0.693711C127.03 -2.11816 72.6477 2.2448 50.6601 39.7696C28.8994 76.9211 7.27154 117.1 0.961468 161.319C-5.13749 204.069 18.4764 229.085 50.4178 250.726C84.4312 273.768 121.205 292.489 161.716 296.545C195.847 299.971 221.877 298.578 250.894 278.256C277.229 259.809 287.73 228.571 296.604 197.807C316.536 128.762 279.035 56.4998 220.649 24.1674C208.686 17.54 200.108 9.63529 187.136 5.6207C182.601 4.21892 171.396 1.24115 164.171 0.693711Z"
                  />
                  <path
                    v-else-if="postIndex % 10 === 8"
                    d="M98.7487 292.053C79.8628 287.085 59.9724 280.633 43.6766 269.686C14.9368 250.363 3.99542 213.318 0.815951 180.685C-5.16113 119.283 21.8519 49.8035 76.841 18.0078C140.646 -18.8799 223.255 3.76027 267.535 59.9376C290.953 89.6435 302.31 119.515 304.277 157.216C305.141 173.735 306.029 189.177 301.836 205.291C296.964 224.025 287.915 234.449 274.076 247.768C249.537 271.378 218.066 287.11 185.076 295.76C175.338 298.314 165.161 298.206 155.141 297.734C152.393 297.601 111.699 295.453 98.7487 292.053Z"
                  />
                  <path
                    v-else-if="postIndex % 10 === 9"
                    d="M177.995 4.62075C122.602 -9.11744 62.1259 14.9753 39.9202 52.5001C17.9435 89.6516 8.37283 111.781 2.00013 156C-4.15935 198.75 12.6619 231.085 44.9202 252.726C79.2712 275.768 111.41 290.489 152.323 294.546C186.792 297.971 213.081 297.578 242.386 277.256C268.982 258.809 284.537 225.765 293.5 195C313.629 125.956 285.465 58.3324 226.5 26.0001C214.418 19.3727 196.298 9.16002 177.995 4.62075Z"
                  />
                  <path
                    v-else-if="postIndex % 10 === 10"
                    d="M166.353 1.0175C144.49 -0.477155 121.396 2.60031 95.5003 13.5C44.3545 35.028 7.006 83 2.00033 120.5C-3.40578 161 5.28695 210.841 26.0003 239C56.7682 280.842 99.3555 299.252 153.685 295.2C210 291 250.618 272.674 266.5 232.5C275 211 298.744 165.617 299.961 133.491C301.096 103.356 279.606 60.6439 258.5 40.5C240.703 23.5057 217.557 16.0563 194.875 6.69684C186.552 3.26397 175.321 2.03765 166.353 1.0256V1.0175Z"
                  />
                  <path
                    v-else
                    d="M101.748 290.053C82.8624 285.085 64.9996 273.667 46.9996 257C19.9996 232 12.9996 217.5 4.81555 178.685C-5.97667 127.5 2.51049 51.2957 57.4996 19.5C121.305 -17.3877 206.719 2.82258 251 58.9999C274.418 88.7058 289.532 110.3 291.5 148C292.363 164.52 286.192 189.887 282 206C277.127 224.734 258.838 246.681 245 260C220.46 283.61 213.066 285.11 180.076 293.76C170.338 296.314 160.16 296.206 150.14 295.733C147.393 295.601 114.699 293.453 101.748 290.053Z"
                  />
                </clipPath>
              </defs>
              <image
                x="0"
                y="0"
                width="304"
                height="298"
                class="h-full w-full object-cover"
                preserveAspectRatio="xMidYMid slice"
                :xlink:href="post.featured_image_url"
                :clip-path="`url(#clip-path-${postIndex})`"
              />
            </svg>
            <strong v-if="post?.date" class="label mb-5 block md:text-s">
              {{ $formatDate(post.date) }}
            </strong>
            <h3
              v-if="post?.title?.rendered"
              class="title mb-4 decoration-dotted decoration-1 underline-offset-4 group-hover:underline"
              v-html="sanitizeHtml(post.title.rendered)"
            />
            <div
              v-if="post?.excerpt?.rendered"
              class="line-clamp-3 decoration-dotted decoration-1 underline-offset-2 group-hover:underline"
              v-html="sanitizeHtml(post.excerpt.rendered)"
            />
          </NuxtLink>
        </li>
      </ul>

      <div
        v-if="
          posts?.pagination?.totalPages && posts?.pagination?.totalPages > 1
        "
        class="mb-7 mt-10 flex justify-center gap-x-3.5 text-xl"
      >
        <button
          :disabled="currentPage === 1"
          class="hover:text-blue xl:mx-1.5"
          :class="currentPage === 1 && 'pointer-events-none text-gray-300'"
          @click="currentPage = currentPage - 1"
        >
          <span class="sr-only">Prev</span>
          <svg width="30" height="30">
            <use href="/icons/icons.svg#arrow-thin" />
          </svg>
        </button>

        <button
          v-for="i in posts?.pagination?.totalPages || 1"
          :key="i"
          class="flex h-7 w-7 items-center justify-center pt-0.5 hover:underline"
          :class="{ 'pointer-events-none font-bold': currentPage === i }"
          :disabled="currentPage === i"
          @click="changePage(i)"
        >
          {{ i }}
        </button>

        <button
          :disabled="currentPage === (posts?.pagination?.totalPages || 1)"
          class="hover:text-blue xl:mx-1.5"
          :class="
            currentPage === (posts?.pagination?.totalPages || 1) &&
            'pointer-events-none text-gray-300'
          "
          @click="currentPage = currentPage + 1"
        >
          <span class="sr-only">Next</span>
          <svg width="30" height="30" class="-scale-x-100">
            <use href="/icons/icons.svg#arrow-thin" />
          </svg>
        </button>
      </div>
    </div>
  </section>
</template>
